import {FieldInputProps, FieldMetaProps} from 'formik';
import {uniqBy} from 'lodash-es';
import {Pot} from '../../../schema-types/Pot';
import {mainPlanTypeType} from '../CreateRecommend/recommendFields';
import {transferFieldsType} from './transferFields';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {getPotInfo, PotInfo} from '../../../utils/getPotInfo';
import {reccNonEditable} from '../CreateRecommend/RecommendSourceDestination/CreateRecommendFooter';
import {POTS_ANNUITY, POTS_PENSION} from '../../../utils/potsFilter';
import {
  getTotalTransferAmount,
  updateTransferSourceIdFactory,
  setTransferCombinedAmounts,
  calculateTransferFirstSubplanAmounts,
  loadSavedTransferPlans,
} from '../hooks/transferHooks';

export {
  getTotalTransferAmount,
  updateTransferSourceIdFactory,
  setTransferCombinedAmounts,
  calculateTransferFirstSubplanAmounts,
  loadSavedTransferPlans,
};

export const transferSourcePots = ({
  sourcePots,
  recommendDetails,
  mainPlanType,
  contact,
  showAllPensions,
}: {
  mainPlanType: mainPlanTypeType;
  sourcePots: Pot[];
  recommendDetails: OVRecommendation | null;
  contact?: string;
  showAllPensions?: boolean;
}): Pot[] => {
  const destPot = recommendDetails?.pots?.find((x) => x.planType === 'destination');
  return uniqBy(sourcePots, 'id').filter((pot) => {
    if (pot.isDummy) {
      const potInfo = getPotInfo(pot, recommendDetails?.id);

      if (pot.originalOvrId && recommendDetails?.id) {
        // return only new plans pertinent to this rec

        if (destPot && pot.id === destPot.pot.id) {
          return false; // if destination is new pot, dont show in sources
        }

        if (potInfo === PotInfo.existingShellPlanThisRec) {
          return false; // show as shell plan if the shell plan was made in this recc
        }
      }
      if (!pot.isShellPlan) {
        // if not shell plan, return false
        return false;
      }
    }
    if (mainPlanType === POTS_PENSION && !showAllPensions) {
      return pot.contact?.id === contact;
    }
    if (mainPlanType === POTS_ANNUITY) {
      return pot.productName?.acronym === 'Pension';
    }
    return true;
  });
};

export interface TransferShellPlanData {
  id: string;
  accountNumber?: string | null;
  planProvider?: string | null;
}

export const transferShellPlans = ({
  transferSources,
}: {
  transferSources: transferFieldsType['transferSources'] | undefined;
}): TransferShellPlanData[] => {
  return Object.values(transferSources || {})
    .filter((x) => x.isShellPlan && x.checked)
    .sort((a, b) => {
      if (a.planProvider?.includes('saltus')) {
        return -1;
      }
      if (b.planProvider?.includes('saltus')) {
        return -1;
      }
      return 0;
    });
};

export const getUnselectedTransferSources = ({
  transferSourcesFieldObj,
  sourcePots,
  recommendDetails,
}: {
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: Pot[];
  recommendDetails: OVRecommendation | null;
}) => {
  // getting a list of all selected sources
  const selectedSourcesKeys = Object.keys(transferSourcesFieldObj.value || {});
  // we must display all unselected sources as destinations
  const unselectedSources = sourcePots
    .filter((p) => !selectedSourcesKeys.includes(p.id))
    .filter((p) => {
      if (recommendDetails) {
        // also filter out new plans
        if (p.isDummy && p.originalOvrId === recommendDetails?.id) {
          return false;
        }
      }
      return true;
    });
  return unselectedSources;
};

export const transferDestinationPots = ({
  transferSourcesFieldObj,
  sourcePots,
  recommendDetails,
  mainPlanType,
  isSppDa,
  showAllPensions,
  contact,
}: {
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: Pot[];
  recommendDetails: OVRecommendation | null;
  mainPlanType: mainPlanTypeType;
  isSppDa: boolean;
  showAllPensions?: boolean;
  contact?: string;
}): Pot[] => {
  const filteredSourcePots = sourcePots.filter((pot) => {
    if (mainPlanType === POTS_PENSION && !showAllPensions) {
      return pot.contact?.id === contact;
    }
    return true;
  });
  const unselectedSources = getUnselectedTransferSources({
    transferSourcesFieldObj,
    sourcePots: filteredSourcePots,
    recommendDetails,
  });

  // Top-level destination pots already saved in this recommendation (excludes subplans)
  const selected =
    recommendDetails?.pots
      ?.filter((x) => x.planType === 'destination' && !x.pot.parent?.id)
      .map((x) => x.pot) || [];

  const destinationPlans =
    recommendDetails?.pots
      ?.filter((x) => x.planType === 'destination')
      .map((x) => x.pot) || [];

  const potsToUse = reccNonEditable(recommendDetails)
    ? destinationPlans
    : unselectedSources;

  // Combine allowed pots with already-saved destinations, excluding dummy pots
  // that were created as new plans for this specific recommendation
  const sources = [...potsToUse, ...selected].filter((x) => {
    if (x.isDummy && x.originalOvrId && x.originalOvrId === recommendDetails?.id) {
      return false;
    }
    return true;
  });

  return uniqBy(sources, 'id').filter((x) => {
    if (isSppDa) {
      return x.isSaltus;
    }
    return true;
  });
};

export const getTransferDestinationFixed = (
  recommendDetails: OVRecommendation | null
): boolean => {
  const selected =
    recommendDetails?.pots?.filter(
      (x) => x.planType === 'destination' && !x.pot.parent?.id
    ) || [];
  return selected.length > 0;
};
